import { useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack, { StackProps } from '@mui/material/Stack';

import Iconify from 'src/components/iconify';

import {
  GoldWithdrawFilters,
  GoldWithdrawFilterKey,
  GoldWithdrawFilterValue,
} from 'src/type/gold-withdraw';
import { shortDateLabel } from 'src/utils/format-time';
import { txStatusLabel } from './utils';

// ----------------------------------------------------------------------

type Props = StackProps & {
  filters: GoldWithdrawFilters;
  onFilters: (name: GoldWithdrawFilterKey, value: GoldWithdrawFilterValue) => void;
  //
  onResetFilters: VoidFunction;
  //
  results: number;
  productOptions: { label: string; value: string }[];
};

export default function RedeemTableFiltersResult({
  filters,
  onFilters,
  //
  onResetFilters,
  //
  results,
  productOptions,
  ...other
}: Props) {
  const shortLabel = shortDateLabel(filters.startDate, filters.endDate);

  const handleRemoveKeyword = useCallback(() => {
    onFilters('name', null);
  }, [onFilters]);

  const handleRemoveService = useCallback(() => {
    onFilters('productId', '');
  }, [filters.productId, onFilters]);

  const handleRemoveStatus = useCallback(() => {
    onFilters('status', 'all');
  }, [onFilters]);

  const handleRemoveDate = useCallback(() => {
    onFilters('startDate', null);
    onFilters('endDate', null);
  }, [onFilters]);

  const productLabel = productOptions.find((option) => option.value === filters.productId)?.label;

  return (
    <Stack spacing={1.5} {...other}>
      <Box sx={{ typography: 'body2' }}>
        <strong>{results}</strong>
        <Box component="span" sx={{ color: 'text.secondary', ml: 0.25 }}>
          รายการ
        </Box>
      </Box>

      <Stack flexGrow={1} spacing={1} direction="row" flexWrap="wrap" alignItems="center">
        {filters.productId !== '' && (
          <Block label="ประเภทสินค้า:">
            <Chip label={productLabel} size="small" onDelete={() => handleRemoveService()} />
          </Block>
        )}

        {filters.status !== 'all' && (
          <Block label="สถานะ:">
            <Chip
              size="small"
              label={txStatusLabel(filters.status)}
              onDelete={handleRemoveStatus}
            />
          </Block>
        )}

        {filters.startDate && filters.endDate && (
          <Block label="วันที่:">
            <Chip size="small" label={shortLabel} onDelete={handleRemoveDate} />
          </Block>
        )}

        {!!filters.name && (
          <Block label="ชื่อ:">
            <Chip label={filters.name.label} size="small" onDelete={handleRemoveKeyword} />
          </Block>
        )}

        <Button
          color="error"
          onClick={onResetFilters}
          startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        >
          ล้าง
        </Button>
      </Stack>
    </Stack>
  );
}

// ----------------------------------------------------------------------

type BlockProps = StackProps & {
  label: string;
};

function Block({ label, children, sx, ...other }: BlockProps) {
  return (
    <Stack
      component={Paper}
      variant="outlined"
      spacing={1}
      direction="row"
      alignItems="center"
      sx={{
        p: 1,
        borderRadius: 1,
        overflow: 'hidden',
        borderStyle: 'dashed',
        ...sx,
      }}
      {...other}
    >
      <Box component="span" sx={{ typography: 'subtitle2' }}>
        {label}
      </Box>

      <Stack spacing={1} direction="row" flexWrap="wrap">
        {children}
      </Stack>
    </Stack>
  );
}
